import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import SectionHeader from '../ui/SectionHeader';
import { apiFeatures } from '../../constants/content';
import { useSectionView } from '../../hooks/useSectionView';

const FeatureIcon = ({ type, darkMode }) => {
  const iconClass = `w-12 h-12 ${darkMode ? 'text-main' : 'text-main'}`;

  const icons = {
    gasless: (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    routing: (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    clock: (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    api: (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )
  };

  return icons[type] || icons.api;
};

export default function APIFeatures() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('api_features', 3);

  return (
    <section
      ref={sectionRef}
      id="features"
      className={`py-24 ${darkMode ? 'bg-main-black' : 'bg-gray-50'}`}
    >
      <div className="mainCont">
        <SectionHeader
          label={t('apiFeatures.label')}
          title={t('apiFeatures.title')}
          titleHighlight={t('apiFeatures.titleHighlight')}
          highlightColor="orange"
          subtitle={t('apiFeatures.subtitle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {apiFeatures.map((feature) => (
            <GlassCard
              key={feature.id}
              hover
              padding="lg"
              className="flex flex-col"
            >
              {/* Icon */}
              <div className={`mb-6 p-3 rounded-2xl inline-flex w-fit ${
                darkMode ? 'bg-main/10' : 'bg-main/10'
              }`}>
                <FeatureIcon type={feature.icon} darkMode={darkMode} />
              </div>

              {/* Headline */}
              <h3 className={`titleFont text-2xl md:text-3xl font-bold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t(`apiFeatures.items.${feature.id}.headline`)}
              </h3>

              {/* Title/tagline */}
              <p className="text-main font-semibold mb-4">
                {t(`apiFeatures.items.${feature.id}.title`)}
              </p>

              {/* Description */}
              <p className={`mb-4 leading-relaxed ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t(`apiFeatures.items.${feature.id}.description`)}
              </p>

              {/* Highlight */}
              <p className={`mt-auto font-medium ${
                darkMode ? 'text-main-orange' : 'text-main-orange'
              }`}>
                {t(`apiFeatures.items.${feature.id}.highlight`)}
              </p>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
}
